import PropTypes from "prop-types";
import { useState, useEffect, useRef } from "react";

// Helpers
import { pushUniqueValue, removeListValue } from "@/Common/helpers/Arrays";
import { groupsBySearchCriteria } from "@/Common/helpers/Search";

// Layouts
import SearchInput from "@/Layouts/components/Basics/SearchInput";

// Components
import AccessListsContent from "./AccessListsContent";
import { AccessListLoading } from "./AccessListLoading";

// MUI
import { Box, Fade, Grid, Stack, Typography } from "@mui/material";
import { useAccessListData } from "./hooks/useAccessListData";
import { LoadingSpinner } from "../LoadingSpinner";
import clsx from "clsx";

// Constants
const LOCATION_STORE_DIGITS = 5;

function useScope(locationId = "") {
	let scope = null;

	try {
		const compare = locationId;
		scope = compare.slice(locationId.length - LOCATION_STORE_DIGITS) === "00000" ? "GLOBAL" : "LOCATION";
	} catch (error) {
		console.log(error);
	}

	return scope;
}

const getSearchCriteria = (type) => {
	switch (type) {
		case "groups":
			return {
				root: null,
				nodes: ["groupName", "groupDescription"],
			};
		case "users":
			return {
				root: "position",
				nodes: ["fullName", "position", "locationName"],
			};
		case "actions":
			return {
				root: "category",
				nodes: ["actionName", "actionDescription"],
			};
		default:
			break;
	}
	return null;
};

const AccessLists = (props) => {
	const {
		access = [],
		accessType = "",
		locationId = "",
		titleField = "",
		descriptionField = "",
		searchable = false,
		onChangeSelection = () => {},
		preloadedData,
	} = props;

	const scope = useScope(locationId);

	const [assigned, setAssigned] = useState([]);
	const [checked, setChecked] = useState([]);
	const [search, setSearch] = useState("");
	const [searchResultsCount, setSearchResultsCount] = useState(0);

	const { root, nodes = [] } = getSearchCriteria(accessType) || {};

	const { data, isLoading, isFetching } = useAccessListData({
		type: accessType,
		locationId,
		scope,
		preloadedData,
	});

	const handleChangeAreaCheckbox = (event, area) => {
		let selections = [...checked];
		const areaItems = data?.filter((item) => item?.[root] === area);
		const [{ list: availableItems = [] } = {}] = groupsBySearchCriteria(areaItems, { root: null, nodes, search });
		let keys = availableItems.filter((item) => !item.filteredOut).map((item) => item._id);

		if (event.target.checked) {
			keys = keys.filter((key) => !assigned.includes(key));
			if (keys.length) {
				keys.forEach((key) => {
					selections = pushUniqueValue(selections, key);
				});
			}
		} else if (keys.length) {
			keys.forEach((key) => {
				selections = removeListValue(selections, key);
			});
		}
		onChangeSelection?.(data.filter((element) => selections.includes(element._id)));
		setChecked(selections);
	};

	const handleChangeCheckbox = (value) => {
		let selections = [...checked];
		if (selections.includes(value)) {
			selections = selections.filter((element) => element !== value);
		} else {
			selections.push(value);
		}

		onChangeSelection?.(data.filter((element) => selections.includes(element._id)));
		setChecked(selections);
	};

	useEffect(() => {
		const currentAssigned = access?.map((action) => action._id) || [];
		setAssigned(currentAssigned);

		return () => {
			setAssigned([]);
			setChecked([]);
			setSearch("");
		};
	}, [access]);

	const intersectionRef = useRef(null);
	const loaderBasicFeedback = `Still loading ${accessType}`;
	const loaderExtraFeedback = accessType === "users" ? " from other locations" : "";
	const loaderFullFeedback = `${loaderBasicFeedback}${loaderExtraFeedback}`;

	if (isLoading) {
		return (
			<>
				<AccessListLoading accessType={accessType} searchable={searchable} />
				<Fade in={isFetching} timeout={400}>
					<Box className="absolute bottom-0 ml-6 mb-6 font-sans text-[14.5px] text-default">
						<LoadingSpinner label={`${loaderBasicFeedback}...`} className="items-center" />
					</Box>
				</Fade>
			</>
		);
	}

	return (
		<>
			{searchable && (
				<Stack justifyContent="center" className={clsx("pt-2", { "pb-8": accessType !== "users" })}>
					<Grid container className="bg-white" justifyContent="center">
						<Grid item xs={6}>
							<SearchInput value={search} onChange={(value) => setSearch(value)} clearSearch={() => setSearch("")} />
							{accessType === "users" && (
								<Typography
									className={clsx("text-center w-full !font-sans !text-[14.5px] pt-[10px]", {
										"text-neutral-gray": search,
										"text-transparent": !search,
									})}
								>
									Showing {searchResultsCount} results
								</Typography>
							)}
						</Grid>
					</Grid>
				</Stack>
			)}
			<Stack ref={intersectionRef} className="overflow-y-auto h-[calc(100%_-_70px)] py-0 mx-2 pl-4 pr-3">
				<AccessListsContent
					data={data}
					intersectionRef={intersectionRef}
					checked={checked}
					selections={assigned}
					accessType={accessType}
					titleField={titleField}
					descriptionField={descriptionField}
					search={search}
					handleChangeCheckbox={handleChangeCheckbox}
					handleChangeAreaCheckbox={handleChangeAreaCheckbox}
					handleChangeSearchResultsCount={setSearchResultsCount}
				/>
			</Stack>
			<Fade in={isFetching} timeout={400}>
				<Box className="absolute bottom-0 ml-6 mb-6 font-sans text-[14.5px] text-default">
					<LoadingSpinner label={`${loaderFullFeedback}...`} className="items-center" />
				</Box>
			</Fade>
		</>
	);
};
AccessLists.propTypes = {
	access: PropTypes.array,
	accessType: PropTypes.oneOf(["groups", "users", "actions", ""]),
	locationId: PropTypes.string,
	titleField: PropTypes.string,
	descriptionField: PropTypes.string,
	searchable: PropTypes.bool,
	onChangeSelection: PropTypes.func,
	preloadedData: PropTypes.array,
};
export default AccessLists;
